/**
 * Shared HTTP error classification for frontend API service clients.
 *
 * WHO: src/services/api/*.ts HTTP clients. `CampaignService` adopts this in
 * PRD001A Wave 2 (001A.7a); `CharacterService` and `SessionService` adopt it in
 * their own later waves (001A.4 FE / 001A.5 FE) — see the caller/handler matrix
 * in shared/docs/Execution/PRD_Program/001A_Closure_Checkpoint.md, "Wave 1 gate".
 * WHAT: Wraps a failed fetch `Response` (or a fetch() that threw before any
 * response existed) into a typed `ApiError` carrying the HTTP status and a
 * coarse `kind` a caller can branch on, instead of string-matching a message.
 * WHERE: src/services/api/ApiError.ts. No network calls of its own.
 * WHEN: Called from a service method's `catch`/`!response.ok` branch, after a
 * `fetch()` either rejects or resolves with a non-2xx `Response`.
 * WHY: PRD001A 001A.7a requires that a 503 or network failure never be turned
 * into an empty result, a false success, or a "forbidden"/"signed-out" state.
 * A caller that only sees `Error.message` cannot reliably tell those apart;
 * `kind` lets it (e.g. render a retryable "service unavailable" state only for
 * `'unavailable' | 'network'`, not for an ordinary `'not_found'`).
 */

/** Coarse category for a failed API call, independent of the human-readable message. */
export type ApiErrorKind =
  | 'unauthenticated' // 401 — not signed in / session invalid
  | 'forbidden' // 403 — signed in, but not permitted
  | 'not_found' // 404
  | 'conflict' // 409
  | 'unavailable' // 5xx — backend or a dependency (auth store, DB) is down
  | 'network' // fetch() threw before a Response existed (offline, DNS, CORS, abort)
  | 'unknown'; // any other non-ok status

export class ApiError extends Error {
  /** HTTP status code, or `null` for a network-level failure with no response. */
  readonly status: number | null;
  readonly kind: ApiErrorKind;
  /** Parsed error body (JSON object, plain text, or `null`) — for diagnostics, not display. */
  readonly detail: unknown;

  constructor(message: string, status: number | null, kind: ApiErrorKind, detail?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.kind = kind;
    this.detail = detail;
  }
}

function classifyStatus(status: number): ApiErrorKind {
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status >= 500) return 'unavailable';
  return 'unknown';
}

/**
 * Safely read a non-ok Response body without throwing.
 *
 * WHY: A 503 from a proxy/load balancer, or a backend crash, often returns an
 * HTML or plain-text body, not JSON. Calling `response.json()` directly on
 * that throws a confusing "Unexpected token" SyntaxError that masks the real
 * HTTP status and replaces it with an unrelated parse error. This tries JSON
 * first, falls back to text, then to `null` — it never throws.
 */
export async function readErrorBody(response: Response): Promise<unknown> {
  const clone = response.clone();
  try {
    return await clone.json();
  } catch {
    try {
      const text = await response.text();
      return text || null;
    } catch {
      return null;
    }
  }
}

/**
 * Extract a human-readable message from a parsed error body.
 *
 * Backend contract (binding, aligned with the API): auth error bodies
 * (401/403/503) are shaped as `{"detail": {"message": "<text>", "code":
 * "<code>"}}` — this reads `body.detail.message` for those. Ordinary FastAPI
 * validation/not-found errors still use a plain string `detail`
 * (`{"detail": "Campaign not found"}`), which is handled too. Falls back to
 * a flat `message` field, then to `fallbackMessage` if nothing usable exists.
 */
function extractMessage(body: unknown, fallbackMessage: string): string {
  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>;
    const nestedDetail = record.detail;
    if (nestedDetail && typeof nestedDetail === 'object') {
      const nestedRecord = nestedDetail as Record<string, unknown>;
      if (typeof nestedRecord.message === 'string' && nestedRecord.message) return nestedRecord.message;
    }
    if (typeof nestedDetail === 'string' && nestedDetail) return nestedDetail;
    if (typeof record.message === 'string' && record.message) return record.message;
  }
  if (typeof body === 'string' && body) return body;
  return fallbackMessage;
}

/**
 * Build an `ApiError` from a non-ok fetch `Response`.
 *
 * @param response The non-ok Response (any status outside 200-299).
 * @param fallbackMessage Used when the body has no usable `detail`/`message`.
 */
export async function apiErrorFromResponse(
  response: Response,
  fallbackMessage: string
): Promise<ApiError> {
  const detail = await readErrorBody(response);
  return new ApiError(extractMessage(detail, fallbackMessage), response.status, classifyStatus(response.status), detail);
}

/**
 * Build an `ApiError` for a `fetch()` call that threw before any `Response`
 * existed (offline, DNS/CORS failure). Always `kind: 'network'`, so callers
 * can treat it the same as a 503 for unavailable-UI purposes.
 *
 * Re-throws an `AbortError` unchanged — that means the caller intentionally
 * cancelled the request (component unmount, superseding request), not a
 * failure to report as unavailable.
 */
export function apiErrorFromNetworkFailure(error: unknown, fallbackMessage: string): ApiError | never {
  if (error instanceof DOMException && error.name === 'AbortError') {
    throw error;
  }
  const message = error instanceof Error ? error.message : fallbackMessage;
  return new ApiError(message, null, 'network', error);
}
